/**
 * ÁRBOL DE UBICACIONES DE UN CENTRO
 *
 * Jerarquía SALA → ZONA → ELEMENTO (recursivo). SUBELEMENTO es legacy:
 * visible y editable, pero no creable y sin hijos. Los campos inmutables
 * (codigo, tipo, id_padre, id_centro, id_cliente) no se ofrecen en edición.
 *
 * La API devuelve lista plana en orden DFS respetando `orden, codigo` por
 * nivel. El cliente filtra los nodos ocultos (ancestors colapsados) sin
 * construir un árbol explícito; `nivel` da la indentación directamente.
 *
 * Creación: «Nueva SALA» desde la raíz; «+ Hijo» desde el contexto del nodo.
 * Expansión: botón ▼/▶ por nodo con hijos; Expandir/Replegar todo global.
 */
import { useMemo, useState, type CSSProperties, type FormEvent, type ReactNode } from 'react';

import { api, type Ubicacion, FalloApi } from '../api.ts';
import {
  CampoTexto,
  Error as ErrorCaja,
  FiltrosResponsive,
  Panel,
  useDatos,
  Vacio,
} from '../componentes/base.tsx';
import { useCentro } from './centro.tsx';
import { useSesion } from '../contexto.tsx';
import { useListaUrl } from '../listaUrl.ts';

// ----------------------------------------------------------------- constantes

const ETIQUETA_TIPO: Record<string, string> = {
  SALA: 'Sala',
  ZONA: 'Zona',
  ELEMENTO: 'Elemento',
  SUBELEMENTO: 'Subelemento',
};

function tipoClase(tipo: string): string {
  if (tipo === 'SALA') return 'info';
  if (tipo === 'ZONA') return 'alerta';
  if (tipo === 'ELEMENTO') return 'neutra';
  return 'secundaria';
}

type TipoCreable = 'SALA' | 'ZONA' | 'ELEMENTO';

/** Tipos válidos como hijos directos de un nodo dado. */
const TIPOS_HIJOS: Partial<Record<string, TipoCreable[]>> = {
  SALA: ['ZONA', 'ELEMENTO'],
  ZONA: ['ELEMENTO'],
  ELEMENTO: ['ELEMENTO'],
};

// ----------------------------------------------------------------- estado

type ModoCreacion =
  | null
  | { tipo: 'sala' }
  | { tipo: 'hijo'; padre: Ubicacion };

// ----------------------------------------------------------------- búsqueda y filtros (FASE 3.3-B5)
//
// Todo frontend, sobre el árbol completo ya recibido (recortado por
// `centroAccesible` en el backend): no hay una segunda llamada que
// pudiera traer algo fuera de ámbito.
//
// No se reordena nada — el árbol no admite sorting de usuario, así que
// no se reutiliza `useListaUrl` con `orden`/`dir`, sólo con `q`/`estado`/
// `tipo` — y el orden estructural (orden, codigo, DFS) se conserva
// siempre porque nunca se hace un nuevo `.sort()`, sólo `.filter()`
// sobre la lista que ya llegó en ese orden.

type EstadoFiltroUbicacion = 'TODAS' | 'ACTIVAS' | 'INACTIVAS';
type TipoFiltroUbicacion = 'TODOS' | 'SALA' | 'ZONA' | 'ELEMENTO';

const DEFAULTS_UBICACIONES = { q: '', estado: 'TODAS', tipo: 'TODOS' } as const;

/**
 * Una fila "coincide" si cumple q AND estado AND tipo. `tipo` no ofrece
 * SUBELEMENTO como opción (no es creable), pero con `tipo=TODOS` un
 * SUBELEMENTO legacy coincide igual que cualquier otra fila — no hay
 * exclusión especial para él, ni falta que hace: sólo desaparece cuando
 * el usuario elige explícitamente un tipo distinto al suyo, exactamente
 * como le pasaría a cualquier tipo que no sea el filtrado.
 */
function coincideUbicacion(
  u: Ubicacion,
  qNorm: string,
  estado: EstadoFiltroUbicacion,
  tipo: TipoFiltroUbicacion,
): boolean {
  if (tipo !== 'TODOS' && u.tipo !== tipo) return false;
  if (estado === 'ACTIVAS' && !u.activo) return false;
  if (estado === 'INACTIVAS' && u.activo) return false;
  if (qNorm !== '' && !u.codigo.toLowerCase().includes(qNorm) && !u.descripcion.toLowerCase().includes(qNorm)) {
    return false;
  }
  return true;
}

/**
 * Ids visibles cuando hay un filtro activo: cada coincidencia, más todos
 * sus ancestros (aunque el ancestro no cumpla q/estado/tipo) — el
 * ancestro se ve como contexto estructural, no como resultado. Un
 * descendiente que no coincide no aparece sólo porque su padre coincida.
 */
function idsVisiblesPorFiltro(
  todas: readonly Ubicacion[],
  qNorm: string,
  estado: EstadoFiltroUbicacion,
  tipo: TipoFiltroUbicacion,
): { visibles: Set<string>; hayCoincidencias: boolean } {
  const porId = new Map(todas.map((u) => [u.id, u] as const));
  const visibles = new Set<string>();
  let hayCoincidencias = false;
  for (const u of todas) {
    if (!coincideUbicacion(u, qNorm, estado, tipo)) continue;
    hayCoincidencias = true;
    let actual: Ubicacion | undefined = u;
    while (actual !== undefined && !visibles.has(actual.id)) {
      visibles.add(actual.id);
      actual = actual.idPadre === null ? undefined : porId.get(actual.idPadre);
    }
  }
  return { visibles, hayCoincidencias };
}

// ----------------------------------------------------------------- página

export function PaginaUbicaciones(): ReactNode {
  const { centro } = useCentro();
  const { puede } = useSesion();
  const idCentro = centro.id;

  const estado = useDatos(
    () => api.get<{ ubicaciones: Ubicacion[] }>(`/api/centros/${idCentro}/ubicaciones`),
    [idCentro],
  );

  const [modoCreacion, setModoCreacion] = useState<ModoCreacion>(null);
  const [editando, setEditando] = useState<Ubicacion | null>(null);
  const [colapsados, setColapsados] = useState<Set<string>>(new Set());

  const listaUrl = useListaUrl(DEFAULTS_UBICACIONES);
  const q = listaUrl.valor('q');
  const filtroEstado = listaUrl.valor('estado') as EstadoFiltroUbicacion;
  const filtroTipo = listaUrl.valor('tipo') as TipoFiltroUbicacion;
  const filtroActivo = !listaUrl.esPorDefecto;

  //  FASE 3.3-D3: badge «Filtros (N)» — nunca `q`.
  const contadorFiltros = [
    filtroEstado !== DEFAULTS_UBICACIONES.estado,
    filtroTipo !== DEFAULTS_UBICACIONES.tipo,
  ].filter(Boolean).length;

  function cerrar() {
    setModoCreacion(null);
    setEditando(null);
  }

  function recargaYCierra() {
    cerrar();
    estado.recargar();
  }

  function toggleColapso(id: string) {
    setColapsados(prev => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  }

  const todas = estado.datos?.ubicaciones ?? [];
  const nodesConHijos = todas.filter(u => u.tieneHijos);

  const qNorm = q.trim().toLowerCase();
  const { visibles: idsPorFiltro, hayCoincidencias } = useMemo(
    () => idsVisiblesPorFiltro(todas, qNorm, filtroEstado, filtroTipo),
    [todas, qNorm, filtroEstado, filtroTipo],
  );

  let visible: Ubicacion[];
  if (filtroActivo) {
    // Con filtro activo, los ancestros necesarios van forzados a
    // expandido: el usuario no puede ocultar una coincidencia
    // colapsando su ancestro mientras el filtro está puesto.
    visible = todas.filter(u => idsPorFiltro.has(u.id));
  } else {
    // Sin filtro: comportamiento de siempre. Un nodo se oculta si su
    // padre está colapsado o también está oculto; la lista llega en
    // DFS, así que el padre aparece siempre antes que sus hijos.
    const ocultos = new Set<string>();
    for (const u of todas) {
      if (u.idPadre !== null && (ocultos.has(u.idPadre) || colapsados.has(u.idPadre))) {
        ocultos.add(u.id);
      }
    }
    visible = todas.filter(u => !ocultos.has(u.id));
  }

  const podeCear = puede('ubicaciones:crear');
  const podeEditar = puede('ubicaciones:editar');
  const modoInactivo = modoCreacion !== null || editando !== null;

  return (
    <>
      {podeCear && !modoInactivo && (
        <div style={{ marginBottom: 16 }}>
          <button className="principal" onClick={() => setModoCreacion({ tipo: 'sala' })}>
            Nueva SALA
          </button>
        </div>
      )}

      {modoCreacion !== null && (
        <div className="panel" style={{ marginBottom: 16 }}>
          <div className="panel-cabecera">
            <h2>
              {modoCreacion.tipo === 'sala'
                ? 'Nueva sala'
                : `Nuevo hijo de ${modoCreacion.padre.codigo}`}
            </h2>
          </div>
          <FormNuevaUbicacion
            idCentro={idCentro}
            padrePreset={modoCreacion.tipo === 'hijo' ? modoCreacion.padre : undefined}
            alCrear={recargaYCierra}
            alCancelar={cerrar}
          />
        </div>
      )}

      {editando !== null && (
        <div className="panel" style={{ marginBottom: 16 }}>
          <div className="panel-cabecera">
            <h2>Editar ubicación</h2>
          </div>
          <FormEditarUbicacion
            datos={editando}
            idCentro={idCentro}
            alEditar={recargaYCierra}
            alCancelar={cerrar}
          />
        </div>
      )}

      <div className="panel">
        <Panel estado={estado}>
          {(d) =>
            d.ubicaciones.length === 0 ? (
              <Vacio titulo="Este centro no tiene ubicaciones">
                {podeCear && (
                  <p className="suave">
                    Crea la primera sala con el botón «Nueva SALA».
                  </p>
                )}
              </Vacio>
            ) : (
              <>
                <FiltrosResponsive
                  contador={contadorFiltros}
                  busqueda={
                    <div className="campo campo-busqueda">
                      <label htmlFor="buscar-ubicacion">Buscar</label>
                      <input
                        id="buscar-ubicacion"
                        type="text"
                        placeholder="Código o descripción…"
                        value={q}
                        onChange={(e) => listaUrl.fijar('q', e.target.value)}
                      />
                    </div>
                  }
                >
                  <div className="campo">
                    <label htmlFor="filtro-estado-ubicacion">Estado</label>
                    <select
                      id="filtro-estado-ubicacion"
                      value={filtroEstado}
                      onChange={(e) => listaUrl.fijar('estado', e.target.value)}
                    >
                      <option value="TODAS">Todas</option>
                      <option value="ACTIVAS">Activas</option>
                      <option value="INACTIVAS">Inactivas</option>
                    </select>
                  </div>
                  <div className="campo">
                    <label htmlFor="filtro-tipo-ubicacion">Tipo</label>
                    <select
                      id="filtro-tipo-ubicacion"
                      value={filtroTipo}
                      onChange={(e) => listaUrl.fijar('tipo', e.target.value)}
                    >
                      <option value="TODOS">Todos</option>
                      <option value="SALA">Sala</option>
                      <option value="ZONA">Zona</option>
                      <option value="ELEMENTO">Elemento</option>
                    </select>
                  </div>
                  {filtroActivo && (
                    <button type="button" className="pequeno" onClick={listaUrl.limpiar}>
                      Limpiar filtros
                    </button>
                  )}
                </FiltrosResponsive>

                {filtroActivo && !hayCoincidencias ? (
                  <Vacio titulo="No hay ubicaciones que coincidan con los filtros" />
                ) : (
                  <>
                    {!filtroActivo && nodesConHijos.length > 0 && (
                      <div style={{ padding: '6px 12px', display: 'flex', gap: 8, borderBottom: '1px solid var(--borde)' }}>
                        <button
                          className="pequeno"
                          onClick={() => setColapsados(new Set())}
                        >
                          Expandir todo
                        </button>
                        <button
                          className="pequeno"
                          onClick={() => setColapsados(new Set(nodesConHijos.map(u => u.id)))}
                        >
                          Replegar todo
                        </button>
                      </div>
                    )}
                    <div className="tabla-desliza">
                      <table>
                        <thead>
                          <tr>
                            <th>Código</th>
                            <th>Tipo</th>
                            <th>Descripción</th>
                            <th className="num col-orden">Orden</th>
                            <th>Estado</th>
                            {(podeCear || podeEditar) && <th />}
                          </tr>
                        </thead>
                        <tbody>
                          {visible.map((u) => (
                            <tr key={u.id} style={{ opacity: u.activo ? 1 : 0.55 }}>
                              <td className="cod">
                                {/*
                                  FASE 3.3-D5: el multiplicador por nivel
                                  vive en CSS (20px de escritorio, 12px
                                  bajo 600px — ver estilos.css), no en JS;
                                  `--nivel` sólo lleva el dato de esta fila.
                                  `nivel`/`idPadre` no cambian, sólo cómo
                                  se traduce a píxeles.
                                */}
                                <span
                                  className="ubicacion-indent"
                                  style={{ '--nivel': u.nivel } as CSSProperties}
                                >
                                  {!filtroActivo && u.tieneHijos && (
                                    <button
                                      type="button"
                                      className="ubicacion-expandir"
                                      onClick={() => toggleColapso(u.id)}
                                      aria-label={`${colapsados.has(u.id) ? 'Expandir' : 'Replegar'} ${u.codigo}`}
                                      aria-expanded={!colapsados.has(u.id)}
                                    >
                                      {colapsados.has(u.id) ? '▶' : '▼'}
                                    </button>
                                  )}
                                  {u.codigo}
                                </span>
                              </td>
                              <td>
                                <span className={`etiqueta ${tipoClase(u.tipo)}`}>
                                  {ETIQUETA_TIPO[u.tipo] ?? u.tipo}
                                </span>
                                {u.tipo === 'SUBELEMENTO' && (
                                  <span
                                    className="etiqueta secundaria"
                                    style={{ marginLeft: 4 }}
                                    title="Tipo legacy: visible pero no creable ni puede recibir hijos"
                                  >
                                    legacy
                                  </span>
                                )}
                              </td>
                              <td>{u.descripcion}</td>
                              <td className="num col-orden">{u.orden}</td>
                              <td>
                                <span className={`etiqueta ${u.activo ? 'exito' : 'neutra'}`}>
                                  {u.activo ? 'ACTIVO' : 'INACTIVO'}
                                </span>
                              </td>
                              {(podeCear || podeEditar) && (
                                <td className="derecha ubicacion-acciones">
                                  {podeCear && u.tipo !== 'SUBELEMENTO' && (
                                    <button
                                      className="pequeno"
                                      onClick={() => setModoCreacion({ tipo: 'hijo', padre: u })}
                                      disabled={modoInactivo}
                                      aria-label={`Añadir hijo a ${u.codigo}`}
                                    >
                                      + Hijo
                                    </button>
                                  )}{' '}
                                  {podeEditar && (
                                    <button
                                      className="pequeno"
                                      onClick={() => setEditando(u)}
                                      disabled={modoInactivo}
                                    >
                                      Editar
                                    </button>
                                  )}
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </>
            )
          }
        </Panel>
      </div>
    </>
  );
}

// -------------------------------------------------------------- formularios

function FormNuevaUbicacion({
  idCentro,
  padrePreset,
  alCrear,
  alCancelar,
}: {
  idCentro: string;
  /** Si está presente, el padre está fijado y el tipo se filtra según su jerarquía. */
  padrePreset?: Ubicacion;
  alCrear: () => void;
  alCancelar: () => void;
}): ReactNode {
  const tiposDisponibles: TipoCreable[] =
    padrePreset === undefined
      ? ['SALA']
      : (TIPOS_HIJOS[padrePreset.tipo] ?? ['ELEMENTO']);

  const [tipo, setTipo] = useState<TipoCreable>(tiposDisponibles[0] ?? 'ELEMENTO');
  const [codigo, setCodigo] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [camposMal, setCamposMal] = useState<Record<string, string>>({});

  const idPadre = padrePreset?.id ?? null;

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    setCamposMal({});
    try {
      await api.post(`/api/centros/${idCentro}/ubicaciones`, {
        tipo,
        codigo: codigo.trim(),
        descripcion: descripcion.trim(),
        idPadre,
      });
      alCrear();
    } catch (err) {
      if (err instanceof FalloApi && err.erroresDeCampo != null) {
        setCamposMal(err.erroresDeCampo);
      } else {
        setError(err);
      }
    } finally {
      setEnviando(false);
    }
  }

  return (
    <form className="form-inline" onSubmit={(e) => { void enviar(e); }}>
      <div className="form-grupo">
        {padrePreset !== undefined && (
          <div className="campo">
            <label>Ubicación padre</label>
            <input
              type="text"
              value={`${padrePreset.codigo} — ${padrePreset.descripcion}`}
              disabled
              aria-label="Ubicación padre (predeterminada por contexto)"
            />
          </div>
        )}

        <div className="campo">
          <label htmlFor="ub-tipo">Tipo</label>
          <select
            id="ub-tipo"
            value={tipo}
            onChange={(ev) => setTipo(ev.target.value as TipoCreable)}
            disabled={enviando || tiposDisponibles.length === 1}
          >
            {tiposDisponibles.map((t) => (
              <option key={t} value={t}>{ETIQUETA_TIPO[t] ?? t}</option>
            ))}
          </select>
          {camposMal['tipo'] !== undefined && (
            <span className="campo-error">{camposMal['tipo']}</span>
          )}
        </div>

        <CampoTexto
          label="Código"
          value={codigo}
          onChange={setCodigo}
          error={camposMal['codigo']}
          disabled={enviando}
          required
        />
        <CampoTexto
          label="Descripción"
          value={descripcion}
          onChange={setDescripcion}
          error={camposMal['descripcion']}
          disabled={enviando}
          required
        />
      </div>
      {error !== null && <ErrorCaja error={error} />}
      <div className="fila-botones">
        <button type="submit" className="principal" disabled={enviando}>
          {enviando ? 'Guardando…' : 'Crear ubicación'}
        </button>
        <button type="button" onClick={alCancelar} disabled={enviando}>
          Cancelar
        </button>
      </div>
    </form>
  );
}

function FormEditarUbicacion({
  datos,
  idCentro,
  alEditar,
  alCancelar,
}: {
  datos: Ubicacion;
  idCentro: string;
  alEditar: () => void;
  alCancelar: () => void;
}): ReactNode {
  const [descripcion, setDescripcion] = useState(datos.descripcion);
  const [activo, setActivo] = useState(datos.activo);
  const [ordenStr, setOrdenStr] = useState(String(datos.orden));
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [camposMal, setCamposMal] = useState<Record<string, string>>({});

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    setCamposMal({});

    const orden = parseInt(ordenStr, 10);
    if (!Number.isInteger(orden)) {
      setCamposMal({ orden: 'El orden debe ser un número entero.' });
      setEnviando(false);
      return;
    }

    try {
      await api.patch(`/api/centros/${idCentro}/ubicaciones/${datos.id}`, {
        descripcion: descripcion.trim(),
        activo,
        orden,
      });
      alEditar();
    } catch (err) {
      if (err instanceof FalloApi && err.erroresDeCampo != null) {
        setCamposMal(err.erroresDeCampo);
      } else {
        setError(err);
      }
    } finally {
      setEnviando(false);
    }
  }

  return (
    <form className="form-inline" onSubmit={(e) => { void enviar(e); }}>
      <div className="form-grupo">
        <div className="campo">
          <label>Tipo</label>
          <input
            type="text"
            value={ETIQUETA_TIPO[datos.tipo] ?? datos.tipo}
            disabled
            aria-label="Tipo (inmutable)"
          />
        </div>
        <div className="campo">
          <label>Código</label>
          <input
            type="text"
            value={datos.codigo}
            disabled
            aria-label="Código (inmutable)"
          />
        </div>
        <CampoTexto
          label="Descripción"
          value={descripcion}
          onChange={setDescripcion}
          error={camposMal['descripcion']}
          disabled={enviando}
          required
        />
        <CampoTexto
          label="Orden"
          value={ordenStr}
          onChange={setOrdenStr}
          error={camposMal['orden']}
          disabled={enviando}
        />
      </div>
      <div className="campo" style={{ marginTop: 4 }}>
        <label
          style={{
            textTransform: 'none',
            fontSize: 14,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            cursor: 'pointer',
            color: 'var(--texto)',
            fontWeight: 'normal',
            marginBottom: 0,
          }}
        >
          <input
            type="checkbox"
            style={{ width: 'auto' }}
            checked={activo}
            onChange={(ev) => setActivo(ev.target.checked)}
            disabled={enviando}
          />
          Ubicación activa
        </label>
      </div>
      {error !== null && <ErrorCaja error={error} />}
      <div className="fila-botones">
        <button type="submit" className="principal" disabled={enviando}>
          {enviando ? 'Guardando…' : 'Guardar cambios'}
        </button>
        <button type="button" onClick={alCancelar} disabled={enviando}>
          Cancelar
        </button>
      </div>
    </form>
  );
}
